import { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { useBulkSalaryRoster, useSaveBulkSalaries } from '../../hooks/useEmployeeSalary';
import {
  DEDUCTED_FIELD_LABELS,
  type BulkSalaryRow,
  type BulkSalaryEntry,
  type DeductedField,
} from '../../services/api/employeeSalary';

/**
 * Phase 84 Plan 84-05 — enter a whole month's salaries in one table.
 *
 * Payroll arrives once a month as a list, so clicking through thirty user
 * detail pages is the wrong shape of work. The table prefills from the previous
 * month (most salaries do not change) and saves in one go.
 *
 * People who started this month sit in a separate section with empty fields:
 * there is nothing to copy forward for them, and hiding them would silently
 * cost someone their first salary.
 */

type RowDraft = {
  netAmount: string;
  grossAmount: string;
  totalCostAmount: string;
  deductedField: DeductedField;
};

const EMPTY_DRAFT: RowDraft = {
  netAmount: '',
  grossAmount: '',
  totalCostAmount: '',
  deductedField: 'gross',
};

function toInput(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

/** Prefer what is already saved for the month; otherwise copy last month. */
function draftFor(row: BulkSalaryRow): RowDraft {
  const source = row.current ?? row.previous;

  // Nobody without a concurrent contractor relationship can have a
  // deduction — pin `deductedField` to 'none' regardless of what last
  // month's record carried. Prefill hazard: last month may well carry
  // 'gross', and copying it forward would re-introduce a deduction the UI
  // no longer lets anyone see or change.
  if (!row.hasConcurrentEmployment) {
    return {
      netAmount: toInput(source?.netAmount),
      grossAmount: toInput(source?.grossAmount),
      totalCostAmount: toInput(source?.totalCostAmount),
      deductedField: 'none',
    };
  }

  if (!source) return EMPTY_DRAFT;
  return {
    netAmount: toInput(source.netAmount),
    grossAmount: toInput(source.grossAmount),
    totalCostAmount: toInput(source.totalCostAmount),
    deductedField: source.deductedField,
  };
}

function parseAmount(raw: string): number | null {
  return raw.trim() === '' ? null : parseFloat(raw);
}

function amountForField(draft: RowDraft): number | null {
  if (draft.deductedField === 'net') return parseAmount(draft.netAmount);
  if (draft.deductedField === 'gross') return parseAmount(draft.grossAmount);
  return parseAmount(draft.totalCostAmount);
}

function isRowEmpty(draft: RowDraft): boolean {
  return (
    parseAmount(draft.netAmount) === null &&
    parseAmount(draft.grossAmount) === null &&
    parseAmount(draft.totalCostAmount) === null
  );
}

/**
 * The server rejects a deducted field whose amount is missing; catch it here
 * first. `amountForField` falls through to `totalCostAmount` for ANY
 * deductedField that is not `net`/`gross` — including `'none'` — so this
 * MUST short-circuit before calling it, or a `'none'` row with a single
 * amount filled in gets flagged invalid and disables Save for the entire
 * roster (most rows are `'none'` after the concurrent-employment gating).
 * Do not "simplify" this guard away.
 */
function isRowInvalid(draft: RowDraft): boolean {
  if (draft.deductedField === 'none') return false;
  return !isRowEmpty(draft) && amountForField(draft) === null;
}

const AMOUNT_COLUMNS = [
  { key: 'netAmount', field: 'net' },
  { key: 'grossAmount', field: 'gross' },
  { key: 'totalCostAmount', field: 'total_cost' },
] as const;

export default function BulkSalaryPage() {
  const now = new Date();
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const { data: roster, isLoading } = useBulkSalaryRoster(period.year, period.month);
  const saveMutation = useSaveBulkSalaries(period.year, period.month);

  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [result, setResult] = useState<string | null>(null);

  // Reload drafts whenever the month (or the fetched roster) changes, so the
  // table never shows one month's figures under another month's heading.
  useEffect(() => {
    if (!roster) return;
    const next: Record<number, RowDraft> = {};
    for (const row of roster) next[row.userId] = draftFor(row);
    setDrafts(next);
    setResult(null);
  }, [roster]);

  const continuing = useMemo(() => roster?.filter((r) => r.hadPreviousMonth) ?? [], [roster]);
  const newcomers = useMemo(() => roster?.filter((r) => !r.hadPreviousMonth) ?? [], [roster]);

  const invalidCount = Object.values(drafts).filter(isRowInvalid).length;
  const filledCount = Object.values(drafts).filter((d) => !isRowEmpty(d)).length;

  const update = (userId: number, patch: Partial<RowDraft>) =>
    setDrafts((prev) => ({ ...prev, [userId]: { ...(prev[userId] ?? EMPTY_DRAFT), ...patch } }));

  // Belt-and-braces against a stale draft surviving a month switch: force
  // 'none' server-side for anyone without a concurrent contractor
  // relationship, regardless of what the draft holds.
  const concurrentByUserId = useMemo(
    () => new Map((roster ?? []).map((r) => [r.userId, r.hasConcurrentEmployment])),
    [roster]
  );

  const handleSave = () => {
    setResult(null);
    const entries: BulkSalaryEntry[] = Object.entries(drafts).map(([userId, draft]) => {
      const id = Number(userId);
      const hasConcurrentEmployment = concurrentByUserId.get(id) ?? false;
      return {
        userId: id,
        netAmount: parseAmount(draft.netAmount),
        grossAmount: parseAmount(draft.grossAmount),
        totalCostAmount: parseAmount(draft.totalCostAmount),
        deductedField: hasConcurrentEmployment ? draft.deductedField : 'none',
      };
    });

    saveMutation.mutate(entries, {
      onSuccess: (res) => {
        const parts = [`Saved ${res.saved}`];
        if (res.cleared > 0) parts.push(`cleared ${res.cleared}`);
        if (res.failed.length > 0) parts.push(`${res.failed.length} failed`);
        setResult(parts.join(', ') + '.');
      },
      onError: (err: Error) => setResult(err.message || 'Failed to save'),
    });
  };

  const renderRow = (row: BulkSalaryRow) => {
    const draft = drafts[row.userId] ?? draftFor(row);
    const invalid = isRowInvalid(draft);

    return (
      <tr key={row.userId} className={invalid ? 'bg-amber-50' : undefined}>
        <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{row.userName}</td>
        {AMOUNT_COLUMNS.map(({ key, field }) => (
          <td key={key} className="px-3 py-2">
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft[key]}
              onChange={(e) => update(row.userId, { [key]: e.target.value } as Partial<RowDraft>)}
              placeholder="—"
              className={`w-32 rounded border px-2 py-1 text-sm text-right ${
                draft.deductedField === field
                  ? 'border-gray-900 font-medium'
                  : 'border-gray-300 text-gray-600'
              }`}
            />
          </td>
        ))}
        <td className="px-3 py-2">
          {row.hasConcurrentEmployment ? (
            <select
              value={draft.deductedField}
              onChange={(e) => update(row.userId, { deductedField: e.target.value as DeductedField })}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            >
              {(Object.keys(DEDUCTED_FIELD_LABELS) as DeductedField[]).map((f) => (
                <option key={f} value={f}>
                  {DEDUCTED_FIELD_LABELS[f]}
                </option>
              ))}
            </select>
          ) : (
            <span
              className="text-gray-400"
              title="No concurrent contractor employment — nothing to deduct from"
            >
              —
            </span>
          )}
        </td>
      </tr>
    );
  };

  const header = (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Person</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Net</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gross</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
          Super gross cost
        </th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
          Deduct from invoice
        </th>
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Monthly salaries</h1>
        <p className="mt-1 text-sm text-gray-500">
          Enter a whole month at once. Amounts are prefilled from the previous month — overwrite
          what changed and clear anyone who should have no salary. Only people who also bill as a
          contractor can have a deduction: for them the bordered column is the amount deducted from
          their freelancer invoice, chosen in the last column. Everyone else&apos;s salary is
          recorded for cost reporting only. Every change is recorded.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
          <input
            type="number"
            value={period.year}
            onChange={(e) => setPeriod((p) => ({ ...p, year: Number(e.target.value) }))}
            className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
          <select
            value={period.month}
            onChange={(e) => setPeriod((p) => ({ ...p, month: Number(e.target.value) }))}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <Spinner />}

      {!isLoading && (roster?.length ?? 0) === 0 && (
        <p className="text-sm text-gray-500">
          Nobody holds an employee-category employment relationship in this month.
        </p>
      )}

      {continuing.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            {header}
            <tbody className="divide-y divide-gray-100">{continuing.map(renderRow)}</tbody>
          </table>
        </div>
      )}

      {newcomers.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-gray-900 mb-1">
            New this month ({newcomers.length})
          </h2>
          <p className="text-sm text-gray-500 mb-2">
            No previous month to copy from, so these are empty — they still need a salary if they
            are owed one.
          </p>
          <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              {header}
              <tbody className="divide-y divide-gray-100">{newcomers.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      )}

      {(roster?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={handleSave} isLoading={saveMutation.isPending} disabled={invalidCount > 0}>
            Save {filledCount} {filledCount === 1 ? 'salary' : 'salaries'}
          </Button>
          {invalidCount > 0 && (
            <span className="text-sm text-amber-700">
              {invalidCount} {invalidCount === 1 ? 'row has' : 'rows have'} a deducted amount that is
              not filled in.
            </span>
          )}
          {result && <span className="text-sm text-gray-700">{result}</span>}
        </div>
      )}

      {saveMutation.data && saveMutation.data.failed.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800 mb-1">Some rows were not saved</p>
          <ul className="text-sm text-red-700 space-y-0.5">
            {saveMutation.data.failed.map((f) => {
              const name = roster?.find((r) => r.userId === f.userId)?.userName ?? `User ${f.userId}`;
              return (
                <li key={f.userId}>
                  {name}: {f.message}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
